import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plug } from 'lucide-react';
import type { CredentialView } from '@agentic-os/contracts';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus } from '@/components/ui/status-badge';
import { api, formatTime } from '@/lib/api';
import { useSession } from '@/lib/session';

const input = 'w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm';

interface McpTool {
  id: string;
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  definitionHash: string;
  toolClass: 'READ' | 'WRITE' | 'ADMIN' | null;
  approval: 'denied' | 'approved' | 'suspended';
  drifted: boolean;
  lastSeenAt: string;
}
interface McpServer {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  status: string;
  lastDiscoveredAt: string | null;
  lastError: string | null;
  tools: McpTool[];
}

/**
 * Outbound MCP registry (ADR-010). New servers and tools are never trusted automatically:
 * every discovered tool starts DENIED; approval pins the tool definition hash and any later
 * change suspends it. Tool descriptions come from external servers and are shown verbatim as
 * untrusted text so administrators see exactly what they approve.
 */
export function McpRegistryPage() {
  const { can } = useSession();
  const manage = can('connections.manage');
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['mcp-servers'],
    queryFn: () => api<{ items: McpServer[] }>('/api/v1/mcp-servers'),
  });
  const creds = useQuery({
    queryKey: ['credentials'],
    queryFn: () => api<{ items: CredentialView[] }>('/api/v1/credentials'),
    enabled: manage,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['mcp-servers'] });
  const [form, setForm] = useState({
    name: '',
    endpoint: 'https://',
    description: '',
    credentialId: '',
  });
  const [classes, setClasses] = useState<Record<string, string>>({});

  const register = useMutation({
    mutationFn: () =>
      api('/api/v1/mcp-servers', {
        method: 'POST',
        body: { ...form, credentialId: form.credentialId || null },
      }),
    onSuccess: () => {
      setForm({ name: '', endpoint: 'https://', description: '', credentialId: '' });
      refresh();
    },
  });
  const act = useMutation({
    mutationFn: (a: { path: string; body?: unknown }) =>
      api(a.path, { method: 'POST', ...(a.body ? { body: a.body } : {}) }),
    onSuccess: refresh,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    register.mutate();
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="MCP Registry"
        description="Outbound MCP servers (remote HTTPS only). Tools are denied until approved with a class; any change to an approved tool suspends it."
      />
      {manage && (
        <Card>
          <CardHeader>
            <CardTitle>Register MCP server</CardTitle>
          </CardHeader>
          <CardBody>
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-4" autoComplete="off">
              <label className="text-xs text-muted">
                Name
                <input
                  className={input}
                  required
                  pattern="[a-z0-9][a-z0-9._\-]{1,62}"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted md:col-span-2">
                Endpoint (Streamable HTTP)
                <input
                  className={input}
                  required
                  type="url"
                  value={form.endpoint}
                  onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted">
                Credential (Bearer)
                <select
                  className={input}
                  value={form.credentialId}
                  onChange={(e) => setForm({ ...form, credentialId: e.target.value })}
                >
                  <option value="">— none —</option>
                  {(creds.data?.items ?? [])
                    .filter((c) => c.status === 'active')
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </label>
              <div className="md:col-span-4">
                <Button type="submit" variant="primary" disabled={register.isPending}>
                  <Plug className="size-3.5" aria-hidden />
                  Register
                </Button>
              </div>
            </form>
            {register.isError && (
              <div className="mt-3">
                <ErrorNote error={register.error} />
              </div>
            )}
          </CardBody>
        </Card>
      )}
      {act.isError && <ErrorNote error={act.error} />}
      {list.isError && <ErrorNote error={list.error} />}
      {(list.data?.items ?? []).map((s) => (
        <Card key={s.id}>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>{s.name}</CardTitle>
              <DomainStatus value={s.status} />
              <Mono>{s.endpoint}</Mono>
            </div>
            {manage && s.status !== 'revoked' && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Button
                  onClick={() => act.mutate({ path: `/api/v1/mcp-servers/${s.id}/discover` })}
                >
                  Discover tools
                </Button>
                {s.status !== 'active' && s.status !== 'draft' && (
                  <Button
                    onClick={() =>
                      act.mutate({
                        path: `/api/v1/mcp-servers/${s.id}/status`,
                        body: { status: 'active' },
                      })
                    }
                  >
                    Activate
                  </Button>
                )}
                {s.status === 'active' && (
                  <Button
                    onClick={() =>
                      act.mutate({
                        path: `/api/v1/mcp-servers/${s.id}/status`,
                        body: { status: 'suspended' },
                      })
                    }
                  >
                    Suspend
                  </Button>
                )}
                <Button
                  variant="danger"
                  onClick={() =>
                    window.confirm(`Revoke ${s.name}? This cannot be undone.`) &&
                    act.mutate({
                      path: `/api/v1/mcp-servers/${s.id}/status`,
                      body: { status: 'revoked' },
                    })
                  }
                >
                  Revoke
                </Button>
              </div>
            )}
          </CardHeader>
          <CardBody className="p-0">
            <p className="px-4 pb-2 text-xs text-muted">
              Last discovery: {formatTime(s.lastDiscoveredAt)}
              {s.lastError && <span className="text-fail"> · {s.lastError}</span>}
            </p>
            <Table>
              <thead>
                <tr>
                  <Th>Tool</Th>
                  <Th>Description (untrusted, verbatim)</Th>
                  <Th>Approval</Th>
                  <Th>Class</Th>
                  {manage && <Th>Decision</Th>}
                </tr>
              </thead>
              <tbody>
                {s.tools.map((t) => (
                  <tr key={t.id}>
                    <Td>
                      <Mono>{t.name}</Mono>
                      <p className="text-xs text-muted" title={t.definitionHash}>
                        hash {t.definitionHash.slice(0, 12)}…
                      </p>
                    </Td>
                    <Td className="max-w-md">
                      <pre className="whitespace-pre-wrap break-words font-mono text-xs text-muted">
                        {t.description || '—'}
                      </pre>
                    </Td>
                    <Td>
                      <DomainStatus value={t.approval} />
                      {t.drifted && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-warn">
                          <AlertTriangle className="size-3" aria-hidden /> Definition changed since
                          approval
                        </p>
                      )}
                    </Td>
                    <Td>{t.toolClass ?? '—'}</Td>
                    {manage && (
                      <Td className="whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <select
                            aria-label={`Class for ${t.name}`}
                            className="rounded-md border border-border bg-surface px-2 py-1 text-xs"
                            value={classes[t.id] ?? t.toolClass ?? ''}
                            onChange={(e) => setClasses({ ...classes, [t.id]: e.target.value })}
                          >
                            <option value="">Class…</option>
                            <option value="READ">READ</option>
                            <option value="WRITE">WRITE</option>
                            <option value="ADMIN">ADMIN</option>
                          </select>
                          <Button
                            disabled={!(classes[t.id] ?? t.toolClass)}
                            onClick={() =>
                              act.mutate({
                                path: `/api/v1/mcp-tools/${t.id}/approval`,
                                body: {
                                  approval: 'approved',
                                  toolClass: classes[t.id] ?? t.toolClass,
                                },
                              })
                            }
                          >
                            Approve
                          </Button>
                          {t.approval !== 'denied' && (
                            <Button
                              variant="danger"
                              onClick={() =>
                                act.mutate({
                                  path: `/api/v1/mcp-tools/${t.id}/approval`,
                                  body: { approval: 'denied' },
                                })
                              }
                            >
                              Deny
                            </Button>
                          )}
                        </div>
                      </Td>
                    )}
                  </tr>
                ))}
                {s.tools.length === 0 && (
                  <tr>
                    <Td colSpan={manage ? 5 : 4} className="text-muted">
                      No tools discovered yet.
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      ))}
      {list.data?.items.length === 0 && (
        <p className="text-sm text-muted">No MCP servers registered.</p>
      )}
    </div>
  );
}
